/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useMemo, useRef, useEffect } from "react";
import { type FilterColumn } from "@CubeComponents/html/SlickGrid1/Modals/FilterButton";
import {
  getConditionMet,
  applyLogicToResult,
  collectAncestorIds,
  type FilterType,
  type QueryOperator,
} from "@CubeComponents/html/SlickGrid1/filterUtils";
import { ColumnData, GridColumn } from "@WebTypes/scheduleDomain";

declare const gantt: any;

const GANTT_COLUMN_FILTER_TYPE_MAP: Record<string, string> = {
  TaskDisp: "Text",
  text: "Text",
  start_date: "Date",
  end_date: "Date",
  duration: "Number",
  actualStartDate: "Date",
  actualEndDate: "Date",
  plannedStartDate: "Date",
  plannedEndDate: "Date",
  plannedDuration: "Number",
  remDuration: "Number",
  progress: "Number",
  totalSlack: "Number",
  critical: "Text",
  "planned%": "Number",
  startVariance: "Number",
  finishVariance: "Number",
  predecessor: "Text",
  successor: "Text",
};

const computeNumericFromTemplate = (task: any, field: string): number | null => {
  const column = gantt.config.columns?.find((c: any) => c.name === field);
  if (!column?.template) return task[field] ?? null;
  const rendered = column.template(task);
  if (rendered === undefined || rendered === null || rendered === "") return null;
  const numericValue = parseFloat(String(rendered).replace("%", "").trim());
  return Number.isNaN(numericValue) ? null : numericValue;
};

export const getGanttTaskFieldValue = (task: any, field: string): any => {
  switch (field) {
    case "start_date":
    case "end_date":
    case "actualStartDate":
    case "actualEndDate":
    case "plannedStartDate":
    case "plannedEndDate":
      return task[field] ? new Date(task[field]).getTime() : null;
    case "progress":
      return task.progress != null ? Math.round(task.progress * 100) : null;
    case "critical":
      if (task.critical) return task.critical === "1" ? "Yes" : "No";
      return gantt.isCriticalTask(task) ? "Yes" : "No";
    case "remDuration":
    case "totalSlack":
    case "planned%":
    case "startVariance":
    case "finishVariance":
    case "plannedDuration":
    case "duration":
      return computeNumericFromTemplate(task, field);
    case "predecessor": {
      const preds = (task.$target || []).map((linkId: any) => {
        const link = gantt.getLink(linkId);
        const predTask = gantt.getTask(link.source);
        return predTask?.TaskDisp;
      });
      return preds.join(", ");
    }
    case "successor": {
      const succs = (task.$source || []).map((linkId: any) => {
        const link = gantt.getLink(linkId);
        const succTask = gantt.getTask(link.target);
        return succTask?.TaskDisp;
      });
      return succs.join(", ");
    }
    default:
      return task[field];
  }
};

interface UseGanttFiltersArgs {
  ganttDataAvailable: boolean;
  savedColumns: ColumnData[];
  rerenderGantt: boolean | null;
}

interface UseGanttFiltersReturn {
  ganttFilterColumns: FilterColumn[];
  ganttTasksDataset: any[];
  handleGanttFilterChange: (filterConditionsObj: Record<string, any>) => void;
  getGanttTaskFieldValue: typeof getGanttTaskFieldValue;
}

export const useGanttFilters = ({
  ganttDataAvailable,
  savedColumns,
  rerenderGantt,
}: UseGanttFiltersArgs): UseGanttFiltersReturn => {
  const ganttFilterEventId = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (ganttFilterEventId.current) {
        gantt.detachEvent(ganttFilterEventId.current);
        ganttFilterEventId.current = null;
      }
    };
  }, []);

  const ganttFilterColumns: FilterColumn[] = useMemo(() => {
    return (
      gantt.config.columns
        ?.filter((col: GridColumn) => col.name && col.name !== "add" && col.name !== "CustomIcon" && !col.hide)
        .map((col: GridColumn) => ({
          field: col.name as string,
          name: col.label || (col.name as string),
          filterType: GANTT_COLUMN_FILTER_TYPE_MAP[col.name as string] || "Text",
          id: col.name as string,
        })) || []
    );
  }, [ganttDataAvailable, savedColumns, rerenderGantt]);

  const ganttTasksDataset = useMemo(() => {
    try {
      return gantt.getTaskByTime?.() || [];
    } catch {
      return [];
    }
  }, [ganttDataAvailable]);

  const handleGanttFilterChange = (filterConditionsObj: Record<string, any>) => {
    if (ganttFilterEventId.current) {
      gantt.detachEvent(ganttFilterEventId.current);
      ganttFilterEventId.current = null;
    }

    const conditionKeys = Object.keys(filterConditionsObj);

    if (conditionKeys.length > 0) {
      const matchedIds: (string | number)[] = [];
      gantt.eachTask((task: any) => {
        let result = false;
        for (let i = 0; i < conditionKeys.length; i++) {
          const condition = filterConditionsObj[conditionKeys[i]!];
          const fieldValue = getGanttTaskFieldValue(task, condition.id);
          const conditionMet = getConditionMet(
            condition.type as FilterType,
            fieldValue,
            condition.option as QueryOperator,
            condition.text,
          );

          if (i === 0) {
            result = conditionMet;
          } else {
            result = applyLogicToResult(result, conditionMet, condition.logic?.toLowerCase() || "and");
          }
        }
        if (result) matchedIds.push(task.id);
      });

      const matchSet = collectAncestorIds(matchedIds, (id) => {
        if (!gantt.isTaskExists(id)) return null;
        const parentId = gantt.getTask(id).parent;
        return parentId && parentId !== 0 ? parentId : null;
      });

      ganttFilterEventId.current = gantt.attachEvent("onBeforeTaskDisplay", function (_id: string, task: any) {
        return matchSet.has(task.id);
      });
    }

    gantt.render();
  };

  return { ganttFilterColumns, ganttTasksDataset, handleGanttFilterChange, getGanttTaskFieldValue };
};
